import { Button, Modal, ModalContent, ModalDescription } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { Coins } from "lucide-react";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { canCheckout, openCheckout } from "~/common/billing/paygate";
import { whyDisabled } from "~/core/access/index";
import { ApiError, api, type CreditBreakdown, type CreditPack, type Org } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { ModalHeader } from "~/core/ui/dialog";
import { Stat } from "~/core/ui/page";

/**
 * Buying credits, from wherever the balance ran out.
 *
 * One dialog for the whole panel rather than a screen, because the moment a
 * person needs it is the moment a button they pressed refused them — and a
 * refusal that navigates away from the thing they were doing turns "top up
 * and try again" into "find the page again". The dialog opens over the
 * screen, the checkout opens over the dialog, and both close onto the same
 * button they started from.
 *
 * Five fixed sizes and no amount field. The processor sells catalogue
 * products, and a customer choosing between five prices decides faster than
 * one asked to name a number.
 */
/**
 * The balance, its lifetimes, and the way to add to it — one card.
 *
 * All three were a card with the number and a line of text under the grid,
 * which reads as belonging to whichever card it happens to sit beneath: on the
 * subscription screen that is the plan, so "2,000 credits expire on the 6th"
 * sat under the plan's renewal date and looked like a sentence about the plan.
 * The action for a figure belongs on the figure's card.
 */
export function CreditsStat({
	org,
	credits,
	state,
	foot,
}: {
	org: Org;
	/** The split by lifetime, once it has loaded. */
	credits?: CreditBreakdown;
	/** The subscription state to trust — the freshly fetched one where the
	 *  screen has it, since `org` is the shell's copy. */
	state: Org["subscription_state"];
	/** What the card says when there is no lifetime to report. */
	foot?: ReactNode;
}) {
	const t = useT();
	const { locale } = useI18n();
	const topUp = useTopUp();
	const admin = useContext(CanPay);
	const lots: ReactNode[] = [];
	if (credits && credits.expiring > 0)
		lots.push(
			<span key="expiring">
				{t("billing.expiring", {
					n: fmt.number(locale, credits.expiring),
					date: fmt.date(locale, credits.expires_at),
				})}
			</span>,
		);
	if (credits && credits.permanent > 0)
		lots.push(<span key="permanent">{t("billing.permanent", { n: fmt.number(locale, credits.permanent) })}</span>);

	return (
		<Stat
			label={t("billing.credits")}
			value={fmt.number(locale, credits?.balance ?? org.credits)}
			foot={lots.length > 0 ? <span className="credit-lots">{lots}</span> : foot}
			// A pack is only for a live plan; a lapsed account renews first, and
			// the plans below are that answer. Buying is the account's money,
			// which a member may look at and not spend.
			action={
				state === "active" ? (
					<Button onClick={() => topUp()} disabled={!admin} title={whyDisabled(admin, t("access.admin"))}>
						{t("topup.open")}
					</Button>
				) : null
			}
		/>
	);
}

const OpenTopUp = createContext<(reason?: string) => void>(() => {});

/**
 * Whether the person may pay: an owner or admin of the account. Its own
 * context because this provider sits above the brand's access context — the
 * dialog opens from every screen, and a member's screen has to know before the
 * click that the packs are not theirs to buy.
 */
const CanPay = createContext(true);

/** Opens the top-up dialog, from anywhere inside the shell. */
export function useTopUp(): (reason?: string) => void {
	return useContext(OpenTopUp);
}

/** Whether the person may buy a pack — the dialog's own answer, for a control that opens it. */
export function useCanPay(): boolean {
	return useContext(CanPay);
}

export function TopUpProvider({
	org,
	admin,
	onPaid,
	children,
}: {
	org: Org;
	admin: boolean;
	onPaid: () => void;
	children: ReactNode;
}) {
	// Null is closed. The reason is what the dialog says first: opened by a
	// refusal it says so, opened by choice it just shows the packs.
	const [reason, setReason] = useState<string | null>(null);
	const open = useCallback((why: string = "choice") => setReason(why), []);

	return (
		<OpenTopUp.Provider value={open}>
			<CanPay.Provider value={admin}>
				{children}
				<TopUpModal org={org} admin={admin} reason={reason} onClose={() => setReason(null)} onPaid={onPaid} />
			</CanPay.Provider>
		</OpenTopUp.Provider>
	);
}

function TopUpModal({
	org,
	admin,
	reason,
	onClose,
	onPaid,
}: {
	org: Org;
	admin: boolean;
	reason: string | null;
	onClose: () => void;
	onPaid: () => void;
}) {
	const t = useT();
	const { locale } = useI18n();
	const open = reason !== null;
	// The same row the plan section reads, asked for only while the dialog is up.
	const billing = useQuery({
		queryKey: [...keys.org(org.id), "billing"],
		queryFn: () => api.billing(org.id),
		enabled: open,
		...read,
	});
	const [busy, setBusy] = useState<string | null>(null);
	const [error, setError] = useState<unknown>(null);

	useEffect(() => {
		if (!open) setError(null);
	}, [open]);

	/*
	 * The checkout is not what grants the credits — the webhook behind it is,
	 * and it lands a second or two after the customer is back here. Waiting
	 * for the balance to move is what lets the dialog close onto a number
	 * that is already right.
	 */
	async function settle(before: number) {
		for (let attempt = 0; attempt < 10; attempt++) {
			await new Promise((done) => setTimeout(done, 1500));
			try {
				const fresh = await api.credits(org.id);
				if (fresh.balance > before) break;
			} catch {
				// A failed poll is not a failed payment.
			}
		}
		onPaid();
	}

	async function buy(pack: CreditPack) {
		setBusy(pack.key);
		setError(null);
		try {
			const checkout = await api.topup(org.id, { pack_key: pack.key, currency: "USD" });
			const paymentId = await openCheckout(checkout.client_secret);
			// Closing the checkout is saying no to a price, not an error.
			if (!paymentId) return;
			toast.success(t("topup.paid", { credits: fmt.number(locale, pack.credits) }));
			await settle(org.credits);
			onClose();
		} catch (e) {
			// The checkout library's own prose is English and not for the screen;
			// the console keeps it for whoever is debugging the failure.
			if (!(e instanceof ApiError)) console.error("checkout failed", e);
			setError(e);
		} finally {
			setBusy(null);
		}
	}

	const packs = billing.data?.packs ?? [];
	const canBuy = org.subscription_state === "active";

	return (
		<Modal open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
			<ModalContent className="topup-dialog">
				<ModalHeader>{t("topup.title")}</ModalHeader>
				<ModalDescription>
					{reason === "credits" ? t("topup.short") : t("topup.body")}{" "}
					{t("topup.rate", { credits: String(billing.data?.credits_per_usd ?? 100) })}
				</ModalDescription>

				<p className="topup-balance">
					<Coins />
					<span>
						{t("topup.balance", { n: fmt.number(locale, org.credits) })}
						{org.credits_expiring > 0 && org.credits_expires_at ? (
							<span className="muted">
								{" · "}
								{t("topup.expiring", {
									n: fmt.number(locale, org.credits_expiring),
									date: fmt.date(locale, org.credits_expires_at),
								})}
							</span>
						) : null}
					</span>
				</p>

				{error ? (
					<div className="banner banner-warn">
						{error instanceof ApiError ? error.localized(locale) : t("topup.checkoutUnavailable")}
					</div>
				) : null}

				{/* A top-up needs a live plan. Saying why beside the packs is the
				    only honest thing to render: five dead buttons are five clicks
				    that go nowhere. */}
				{canBuy ? null : (
					<div className="banner banner-warn">
						{t(org.subscription_state === "expired" ? "topup.expired" : "topup.needsPlan")}
					</div>
				)}
				{canCheckout ? null : <p className="hint">{t("billing.noCheckout")}</p>}
				{/* Whose button this is, said beside the packs rather than by a
				    refusal after the click. */}
				{admin ? null : <p className="hint">{t("access.admin")}</p>}

				<div className="topup-packs">
					{packs.map((pack) => {
						const price = Number(pack.price_usd_micros) / 1_000_000;
						const sellable = pack.price_id_usd !== null;
						return (
							<Button
								variant="default"
								key={pack.key}
								type="button"
								className="topup-pack h-auto flex-col items-start justify-start whitespace-normal p-(--s-8) text-left"
								disabled={busy !== null || !canBuy || !canCheckout || !sellable || !admin}
								title={whyDisabled(admin, t("access.admin"))}
								onClick={() => buy(pack)}
							>
								<strong className="num">{fmt.number(locale, pack.credits)}</strong>
								<span className="muted">{t("topup.credits")}</span>
								<span className="topup-price num">
									{"$"}
									{fmt.number(locale, price)}
								</span>
								{busy === pack.key ? <span className="muted">{t("login.working")}</span> : null}
							</Button>
						);
					})}
				</div>

				<p className="hint">{t("topup.neverExpire")}</p>
				<div className="row-actions" style={{ justifyContent: "flex-end" }}>
					<Button variant="default" onClick={onClose}>
						{t("topup.later")}
					</Button>
				</div>
			</ModalContent>
		</Modal>
	);
}
